import React, { useEffect, useState } from "react";
import { Avatar, Form, Upload, Tooltip, message } from "antd";
import type { UploadProps } from "antd";
import { RcFile } from "antd/es/upload";
import Button from "../Button/Button";
import UserProfile from "../../../src/assets/img/profile-icon.svg";

interface LogoProps {
  logoUrl?: string; // Ensure it's a string (or undefined)
  imageName: string;
  open?: boolean;
  placeholder?: string;
  onFileSelect?: (file: RcFile | null) => void;
}

const ImageUpload: React.FC<LogoProps> = ({
  logoUrl,
  imageName,
  open,
  placeholder = "Upload an image",
  onFileSelect,
}) => {
  const [fileList, setFileList] = useState<RcFile[]>([]);
  const [validationError, setValidationError] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);

  const handleUpload = async (file: RcFile) => {
    const isValid = await validateImage(file);
    if (isValid) {
      setFileList([file]);
      // Pass the file to parent
      if (onFileSelect) {
        onFileSelect(file);
      }
      return true;
    }
    return false;
  };

  const uploadProps: UploadProps = {
    fileList,
    beforeUpload: async (file) => {
      const result = await handleUpload(file);
      return false; // Always return false to prevent automatic upload
    },
    onRemove: () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
      setFileList([]);
      setPreviewUrl(null);
      setValidationError(null);
      // Clear the file in parent
      if (onFileSelect) {
        onFileSelect(null);
      }
    },
  };

  useEffect(() => {
    if (!open) setFileList([]);
  }, [open]);

  // When component unmounts, clean up any object URLs
  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const validateImage = (file: RcFile): Promise<boolean> => {
    const isSupportedFormat = ["image/jpeg", "image/png"].includes(file.type);
    if (!isSupportedFormat) {
      setValidationError("Format not supported: Please upload a JPEG or PNG");
      message.error("Format not supported: Please upload a JPEG or PNG");
      return Promise.resolve(false);
    }

    const isLessThan2MB = file.size / 1024 / 1024 < 2;
    if (!isLessThan2MB) {
      setValidationError("File too large: Please select an image under 2MB");
      message.error("File too large: Please select an image under 2MB");
      return Promise.resolve(false);
    }

    return new Promise<boolean>((resolve) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        const { width, height } = img;

        const isMinDimension = width >= 200 && height >= 200;
        const isMaxDimension = width <= 500 && height <= 500;

        if (!isMinDimension) {
          setValidationError(
            "Image too small: Please choose an image that's at least 200×200 pixels"
          );
          message.error(
            "Image too small: Please choose an image that's at least 200×200 pixels"
          );
          resolve(false);
        } else if (!isMaxDimension) {
          setValidationError(
            "Image too large: Please choose an image no bigger than 500×500 pixels"
          );
          message.error(
            "Image too large: Please choose an image no bigger than 500×500 pixels"
          );
          resolve(false);
        } else {
          setValidationError(null);
          setPreviewUrl(URL.createObjectURL(file));
          resolve(true);
          onFileSelect(null);
        }

        URL.revokeObjectURL(img.src);
      };
    });
  };

  const tooltipText = (
    <div className="text-xs leading-relaxed">
      <strong> Picture Guidelines:</strong>
      <br />
      • Square image (1:1 ratio), minimum 200×200 pixels (400×400 recommended)
      <br />
      • Maximum file size: 2MB
      <br />
      • Supported formats: JPEG, PNG
      <br />• Clear facial image recommended for best recognition
    </div>
  );

  return (
    <div className="mb-[20px]">
      <Form.Item name={imageName} className="upload-input-col relative">
        <Tooltip title={tooltipText} placement="topLeft">
          <Upload {...uploadProps}>
            {!fileList.length && !logoUrl && (
              // <span className="text-[#005a90] text-xs mt-1 absolute top-[15px] left-[20px]">
              <span className="text-[#fff] text-xs mt-1 absolute top-[15px] left-[20px]">
                {placeholder}
              </span>
            )}
            <Button
              type="button"
              buttonText="Upload"
              padding="py-[8px] px-[18px]"
              text="text-[12px] 2xl:text-sm"
            />
          </Upload>
        </Tooltip>
      </Form.Item>
      {validationError && (
        <div className="text-red-500 text-xs mt-1">{validationError}</div>
      )}
      {/* Display Avatar or Placeholder Image */}
      {(fileList.length > 0 || logoUrl) && (
        <Avatar
          src={
            fileList.length > 0
              ? URL.createObjectURL(fileList[0]) // Show selected image
              : logoUrl || undefined // Show logo if available
          }
          icon={
            !logoUrl && fileList.length === 0 ? (
              <img src={UserProfile} alt="User Profile" />
            ) : undefined
          }
          className="p-[3px] bg-primary rounded-[10px] object-cover"
          size={100}
        />
      )}
    </div>
  );
};

export default ImageUpload;
